import { IonTitle, IonToolbar, useIonModal } from '@ionic/react';
import {
  persistMeasureFormOpen,
  shouldRestoreMeasureForm,
  useMeasuresPage,
} from '@lidea/analysis/shared/data-access';
import {
  useAnalysis,
  usePendingSyncPointIds,
  usePoint,
} from '@lidea/shared/data-access/analysis';
import { useCampaignStore } from '@lidea/shared/data-access/core';
import { plotKeys } from '@lidea/shared/data-access/plots';
import { MobilePage } from '@lidea/shared/ui/layout';
import { GeolocateControl, PlotMap, PlotMapType } from '@lidea/shared/ui/map';
import { isPicturePickerActive } from '@lidea/shared/util/core';
import { MeasureSummary } from '@lidea/shared/util/types';
import { usePrevious } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, useHistory } from 'react-router-dom';

import MeasureModal from '../../components/measure-modal/measure-modal';
import MeasuresToolbar from '../../components/measures-toolbar/measures-toolbar';
import PlotToolbar from '../../components/plot-toolbar/plot-toolbar';
import PointModal from '../../components/point-modal/point-modal';
import styles from './measures.module.css';

/* eslint-disable-next-line */
export interface MeasuresProps
  extends RouteComponentProps<{
    id: string;
    analysisId: string;
  }> {}

export function Measures(props: MeasuresProps) {
  const { id, analysisId } = props.match.params;
  const history = useHistory();
  const { t } = useTranslation(['common', 'plot', 'analysis'], {
    useSuspense: false,
  });

  const plotId = Number(id);
  const analysisIdNum = Number(analysisId);
  const queryClient = useQueryClient();
  const campaignId = useCampaignStore((s) => s.campaignId);
  const didRestoreFormRef = useRef(false);

  const {
    plot,
    zoning,
    updateMeasure,
    selectedPoint,
    setSelectedPoint,
    selectedMeasure,
    handlePointClick,
    handleSubmitMeasures,
    pointsReady,
  } = useMeasuresPage({
    plotId,
    analysisId: analysisIdNum,
  });

  const { pendingSyncPointIds, syncingPointIds } = usePendingSyncPointIds({
    plotId,
    analysisId: analysisIdNum,
  });

  const analysis = useAnalysis({
    plotId,
    analysisId: analysisIdNum,
  });
  const selectedPointFeature = usePoint({
    pointId: selectedPoint,
    analysisId: analysisIdNum,
  });

  const selectedMeasureSummary: MeasureSummary = useMemo(() => {
    if (!selectedMeasure) {
      return [];
    }
    const summary: MeasureSummary = [];

    selectedMeasure?.dynamic.forEach((measure) => {
      if (measure.summary) {
        summary.push({ ...measure.summary, label: measure.label });
      }
    });

    return summary;
  }, [selectedMeasure]);

  const measureSubmitPending =
    updateMeasure.isPending && !updateMeasure.isPaused;

  const [presentMeasureModal, dismissMeasureModal] = useIonModal(MeasureModal, {
    analysisId: analysisIdNum,
    plotId,
    measure: selectedMeasure,
    pending: measureSubmitPending,
    zone: selectedPointFeature?.properties.zone,
    onSubmit: (data: any) => {
      persistMeasureFormOpen(analysisIdNum, false);
      handleSubmitMeasures(data);
      void dismissMeasureModal(null, 'confirm');
    },
    dismiss: () => {
      persistMeasureFormOpen(analysisIdNum, false);
      void dismissMeasureModal(null, 'cancel');
    },
  });

  const openMeasureForm = () => {
    persistMeasureFormOpen(analysisIdNum, true);
    presentMeasureModal({
      cssClass: 'page-modal page-modal-keyboard',
      // Only Annuler / Valider may close (roles below). Blocks backdrop, swipe, Esc, back.
      backdropDismiss: false,
      canDismiss: async (_data, role) =>
        role === 'cancel' || role === 'confirm',
      componentProps: {
        pending: measureSubmitPending,
        measure: selectedMeasure,
        zone: selectedPointFeature?.properties.zone,
      },
    });
  };

  const handleSelectPoint = (id: number) => {
    if (isPicturePickerActive()) {
      return;
    }
    handlePointClick(id);
  };

  const handleMapClickMiss = () => {
    if (isPicturePickerActive()) {
      return;
    }
    setSelectedPoint(null);
  };

  const handleGoToPlots = async () => {
    await queryClient.refetchQueries({
      queryKey: plotKeys.detail(plotId, campaignId).queryKey,
    });
    history.push(`/search/plots/${id}`);
  };

  const previousIsSuccess = usePrevious(updateMeasure.isSuccess);
  const previousIsPaused = usePrevious(updateMeasure.isPaused);

  useEffect(() => {
    if (updateMeasure.isSuccess && !previousIsSuccess) {
      persistMeasureFormOpen(analysisIdNum, false);
      updateMeasure.reset();
      setSelectedPoint(null);
      return;
    }

    if (updateMeasure.isPaused && !previousIsPaused) {
      persistMeasureFormOpen(analysisIdNum, false);
      setSelectedPoint(null);
    }
  }, [
    analysisIdNum,
    updateMeasure.isSuccess,
    updateMeasure.isPaused,
    previousIsSuccess,
    previousIsPaused,
    updateMeasure.reset,
    setSelectedPoint,
  ]);

  // Soft-reload recovery: reopen the measure form if it was open moments ago.
  useEffect(() => {
    if (didRestoreFormRef.current) {
      return;
    }
    if (!shouldRestoreMeasureForm(analysisIdNum)) {
      return;
    }
    if (selectedPoint == null || !selectedMeasure) {
      return;
    }
    didRestoreFormRef.current = true;
    openMeasureForm();
    // openMeasureForm closes over latest measure/zone — run once when data is ready.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisIdNum, selectedPoint, selectedMeasure]);

  return (
    <MobilePage
      defaultBackHref={`/search/plots/${id}`}
      headerExtension={
        <>
          <PlotToolbar plotId={plotId} />
          <MeasuresToolbar
            status={analysis?.analysis.status}
            title={t(`plot:yield_analysis`)}
          />
          {pointsReady && !measureSubmitPending ? (
            <IonToolbar
              color="success"
              style={{ '--min-height': 10, paddingBlock: 8 }}
              onClick={handleGoToPlots}
            >
              <IonTitle>{t('analysis:points_ready')}</IonTitle>
            </IonToolbar>
          ) : null}
        </>
      }
    >
      <div className={styles['mapWithSheet']}>
        <PlotMap
          mobile={true}
          pendingSyncPointIds={pendingSyncPointIds}
          plot={plot.data}
          points={zoning.data?.points}
          selectedPointId={selectedPoint ? selectedPoint : undefined}
          syncingPointIds={syncingPointIds}
          type={PlotMapType.Zoning}
          zoning={zoning.data?.satzones}
          onMapClickMiss={handleMapClickMiss}
          onPointClick={handleSelectPoint}
        >
          <GeolocateControl />
        </PlotMap>

        {selectedPoint != null ? (
          <div className={styles['pointSheet']}>
            <PointModal
              analysisId={analysisIdNum}
              pointNumber={selectedPointFeature?.properties.zone}
              summary={selectedMeasureSummary}
              onClickInput={openMeasureForm}
            />
          </div>
        ) : null}
      </div>
    </MobilePage>
  );
}

export default Measures;
